import { Skeleton } from "@bednbody/ui/components/skeleton";
// "Why is this ₱349.50 and not ₱698?" — CASH § Visibility's "balance update".
//
// ⚠ A DIALOG, AND THE PACKAGE ONE. Six numbers cannot become six columns on a
// table that already carries seven, and the question is per-row and occasional
// rather than something to scan. `Dialog` from `packages/ui` rather than a
// hand-rolled overlay: frontend-standards §6, and the enter/exit animation is
// built into the component tree.
//
// ⚠ READ-ONLY, AND STRUCTURALLY SO. Nothing here can change a number. The
// retention is append-only at the database (`trg_tcr_protect`, 0203), the split
// is committed inside `confirm_cash_received`, and an admin arbitrating a
// dispute needs to SEE the arithmetic, not adjust it. A correction to a wrong
// split is a migration with a reason attached, never a form.

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@bednbody/ui/components/dialog";
import { cn } from "@bednbody/ui/lib/utils";
import { useCashRetention } from "./cash.hooks";
import { retentionBreakdown, retentionSummary } from "./cash.retention";

const peso = (n: number) => `₱${n.toLocaleString("en-PH", { minimumFractionDigits: 2 })}`;

export function CashRetentionDialog({
  bookingId,
  bookingRef,
  therapistName,
  allowed,
  onClose,
}: {
  /** Null closes the dialog — the page owns which row is open. */
  bookingId: string | null;
  bookingRef: string | null;
  therapistName: string | null;
  /** Roles `0203` grants the retention table to. See `cash.retention.ts`. */
  allowed: boolean;
  onClose: () => void;
}) {
  const { data, isLoading, isError } = useCashRetention(bookingId, allowed);
  const lines = data ? retentionBreakdown(data) : [];

  return (
    <Dialog open={bookingId !== null} onOpenChange={(open) => (open ? null : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cash Handling split</DialogTitle>
          <DialogDescription>
            {therapistName ?? "The therapist"}
            {bookingRef ? ` · ${bookingRef}` : ""} — how the client&apos;s cash was divided at Cash
            Received.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <Skeleton className="h-40 motion-reduce:animate-none" />
        ) : isError ? (
          <p className="text-sm text-destructive">
            Couldn&apos;t load the split — check your connection and try again.
          </p>
        ) : !data ? (
          // ⚠ NOT ZEROES. A remittance opened before 0203 has no retention, and
          // showing six ₱0.00 rows would claim she was owed nothing and kept
          // nothing — a statement about her money that nothing recorded.
          <p className="text-sm text-muted-foreground">
            No split was recorded for this remittance. It was opened before the excess rule shipped,
            and the amount is the one two people counted at the time.
          </p>
        ) : (
          <div className="space-y-3">
            <ul className="list-none divide-y divide-border p-0">
              {lines.map((line) => (
                <li
                  key={line.label}
                  className={cn(
                    "flex items-start justify-between gap-4 py-2",
                    line.kind === "result" && "font-medium",
                  )}
                >
                  <div className="min-w-0">
                    <p className="text-sm text-foreground">{line.label}</p>
                    <p className="text-xs text-muted-foreground">{line.note}</p>
                  </div>
                  <span
                    className={cn(
                      "shrink-0 tabular-nums text-sm",
                      line.kind === "deduction" && "text-muted-foreground",
                    )}
                  >
                    {/* A deduction is shown as one. The four terms above the
                        rule are subtractions in the approved formula, and a
                        column of unsigned numbers would read as a sum. */}
                    {line.kind === "deduction" ? `− ${peso(line.value)}` : peso(line.value)}
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-muted-foreground">{retentionSummary(data)}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
